/**
 * PRDProgressDisplay - Presentational component for PRD generation progress.
 *
 * Purely presentational - no state management, just renders based on props.
 * Displays:
 * - Loading states (hydrating, loading existing, starting new)
 * - Generation progress with phase indicators
 * - Completion state with Review button
 * - Error states with retry/start over actions
 */

import { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@heroui/react';

import { useSSEStore } from '~/stores/sseStore';
import { useUIStore } from '~/stores/uiStore';

import { LoadingSpinner } from '~/components/LoadingSpinner';

export interface PRDProgressDisplayProps {
  // State indicators
  state: string;
  projectId: string;
  isGenerating: boolean;
  isComplete: boolean;
  hasError: boolean;

  // Data
  error: string | null;
  sessionState: string | null;

  // Actions
  onRetry: () => void;
  onStartOver: () => void;
}

/**
 * Phase dots indicator - visual representation of progress through 4 phases.
 */
function PhaseDots({
  currentPhase,
  isComplete,
  totalPhases,
}: {
  currentPhase: number;
  isComplete: boolean;
  totalPhases: number;
}): JSX.Element {
  return (
    <div className="flex items-center justify-center gap-3 mb-4">
      <div className="flex gap-1.5">
        {Array.from({ length: totalPhases }, (_, i) => i + 1).map(n => (
          <span
            key={n}
            className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${
              isComplete || n < currentPhase
                ? 'bg-success-500'
                : n === currentPhase
                  ? 'bg-success-500 animate-pulse'
                  : 'bg-default-300 dark:bg-default-600'
            }`}
          />
        ))}
      </div>
      {!isComplete && (
        <span className="text-sm text-muted">
          Phase {currentPhase} of {totalPhases}
        </span>
      )}
    </div>
  );
}

/**
 * Extract phase info from SSE events.
 */
function usePhaseInfo(projectId: string): {
  currentPhase: number;
  isSkipped: boolean;
  phaseLabel: string;
  phaseName: string;
  totalPhases: number;
} | null {
  const eventsByProject = useSSEStore(state => state.eventsByProject);

  const projectEvents = eventsByProject[projectId] || [];
  const phaseEvents = projectEvents.filter(e => e.event === 'prd_phase');

  if (phaseEvents.length > 0) {
    const latest = phaseEvents[phaseEvents.length - 1];
    return latest.data as {
      currentPhase: number;
      isSkipped: boolean;
      phaseLabel: string;
      phaseName: string;
      totalPhases: number;
    };
  }

  return null;
}

/**
 * Presentational component for PRD generation progress.
 */
export function PRDProgressDisplay({
  error,
  hasError,
  isComplete,
  isGenerating,
  onRetry,
  onStartOver,
  projectId,
  sessionState,
  state,
}: PRDProgressDisplayProps): JSX.Element {
  const navigate = useNavigate();
  const setProjectAssistantExpanded = useUIStore(state => state.setProjectAssistantExpanded);

  const phaseInfo = usePhaseInfo(projectId);

  // Loading states (hydrating, loading existing, starting new)
  if (state === 'hydrating' || state === 'loadingExisting') {
    const message = state === 'hydrating' ? 'Loading session...' : 'Loading existing session...';
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-sm text-muted">{message}</p>
        </div>
      </div>
    );
  }

  // Starting new - show simplified phase progress
  if (state === 'startingNew') {
    const currentPhaseNum = phaseInfo?.currentPhase ?? 1;
    const totalPhases = phaseInfo?.totalPhases ?? 4;
    const phaseLabel = phaseInfo?.phaseLabel ?? 'Starting PRD generation...';

    return (
      <div className="flex-1 flex flex-col p-8">
        <div className="max-w-md mx-auto w-full">
          <h3 className="text-lg font-semibold text-foreground mb-4 text-center">Generating PRD</h3>
          <div className="flex justify-center mb-6">
            <LoadingSpinner />
          </div>
          <PhaseDots currentPhase={currentPhaseNum} totalPhases={totalPhases} isComplete={false} />
          <p className="text-sm text-foreground text-center font-medium">{phaseLabel}</p>
        </div>
      </div>
    );
  }

  // Generating or complete state
  if (isGenerating || isComplete || state === 'readyForFinalization') {
    const currentPhaseNum = isComplete ? 4 : (phaseInfo?.currentPhase ?? 1);
    const totalPhases = phaseInfo?.totalPhases ?? 4;
    const phaseLabel = phaseInfo?.phaseLabel ?? 'Generating final PRD...';

    const getTitle = (): string => {
      if (isComplete) return 'PRD Ready for Review';
      if (isGenerating) return 'Generating PRD';
      return 'Preparing PRD';
    };

    return (
      <div className="flex-1 flex flex-col p-8">
        <div className="max-w-md mx-auto w-full">
          <h3 className="text-lg font-semibold text-foreground mb-4 text-center">{getTitle()}</h3>

          {/* Spinner - show while generating */}
          {isGenerating && !isComplete && (
            <div className="flex justify-center mb-6">
              <LoadingSpinner />
            </div>
          )}

          <PhaseDots
            currentPhase={currentPhaseNum}
            totalPhases={totalPhases}
            isComplete={isComplete}
          />

          {/* Completion or progress message */}
          {isComplete ? (
            <div className="flex flex-col items-center gap-6">
              <div className="flex items-center justify-center gap-2 text-sm text-foreground font-medium">
                <span className="text-success-500">✓</span>
                <span>PRD generated successfully!</span>
              </div>

              <Button
                color="primary"
                size="lg"
                fullWidth
                onPress={() => {
                  setProjectAssistantExpanded(false);
                  void navigate(`/projects/${projectId}/review`);
                }}
              >
                Review PRD with AI
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {sessionState === 'error' ? (
                <div className="mt-4">
                  <p className="text-sm text-danger-500 text-center font-medium mb-4">
                    {error || 'PRD generation failed.'}
                  </p>
                  <div className="flex flex-col gap-2">
                    <Button color="primary" onClick={onRetry} className="w-full">
                      Retry Generation
                    </Button>
                    <Button variant="ghost" onClick={onStartOver} className="w-full">
                      Start Over
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-foreground text-center font-medium">{phaseLabel}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className="flex-1 flex flex-col p-8">
        <div className="max-w-md mx-auto w-full text-center">
          <h3 className="text-lg font-semibold text-error mb-4">Error</h3>
          <p className="text-sm text-muted mb-6">{error || 'An error occurred'}</p>
          <div className="flex flex-col gap-2">
            <Button color="primary" onClick={onRetry} className="w-full">
              Retry
            </Button>
            <Button variant="ghost" onClick={onStartOver} className="w-full">
              Start Over
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Fallback - should not reach here
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center text-muted">
        <p className="text-sm">No active session. Please start a new PRD generation.</p>
      </div>
    </div>
  );
}
